const BASE = '/api';

function getToken() {
  return localStorage.getItem('acts_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }

  const ct = res.headers.get('content-type') || '';
  if (
    ct.includes('text/csv')        ||
    ct.includes('application/zip') ||
    ct.includes('application/pdf') ||
    ct.includes('application/octet-stream') ||
    ct.includes('spreadsheetml')
  ) return res.blob();

  return res.json();
}

function getWeekStart(dateStr) {
  const [y, m, d] = (dateStr || new Date().toISOString().slice(0, 10)).split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay();
  const toMon = dow === 0 ? 6 : dow - 1;
  dt.setDate(dt.getDate() - toMon);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}

export const api = {
  // Auth
  login: (username, password) =>
    request('/auth/login', { method: 'POST', body: { username, password } }),

  // Jobs
  submitJob: (data) => request('/jobs', { method: 'POST', body: data }),
  getCarHistory: (stock) => request(`/jobs/car-history?stock_number=${encodeURIComponent(stock)}`),
  getJobs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/jobs${qs ? '?' + qs : ''}`);
  },
  // Deliberately not customer-scoped: staff are paid for the whole week's
  // work, whichever customer it was billed to.
  getStaffWeekly: (week_number, week_year) =>
    request(`/jobs/staff-weekly?week_number=${week_number}&week_year=${week_year}`),
  // Live daily production meter for hourly workers → { date, production, wage, goal, jobCount }
  getMyProduction: (date) => request(`/jobs/my-production${date ? `?date=${date}` : ''}`),
  // Admin: today's meter for every active hourly worker → { date, workers: [...] }
  getHourlyProductionAll: (date) => request(`/jobs/hourly-production-all${date ? `?date=${date}` : ''}`),
  updateJob: (id, data) => request(`/jobs/${id}`, { method: 'PUT', body: data }),
  updateBillingLine: (id, data) => request(`/jobs/billing-lines/${id}`, { method: 'PUT', body: data }),
  deleteJob: (id) => request(`/jobs/${id}`, { method: 'DELETE' }),
  getSummary: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/jobs/summary${qs ? '?' + qs : ''}`);
  },
  exportJobs: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const blob = await request(`/jobs/export${qs ? '?' + qs : ''}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `acts-jobs.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
  exportBackup: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const blob = await request(`/jobs/backup${qs ? '?' + qs : ''}`);
    const today = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `acts-backup-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
  getRaiseProgress: () => request('/jobs/raise-progress'),
  getFirstOccurrences: () => request('/jobs/first-occurrences'),

  // Clock
  clockStatus:   () => request('/clock/status'),
  clockIn:   (coords) => request('/clock/in',  { method: 'POST', body: coords }),
  clockOut:  (body)   => request('/clock/out', { method: 'POST', body: body }),
  clockReset: ()      => request('/clock/reset', { method: 'DELETE' }),
  updateChecklist: (checklist) => request('/clock/checklist', { method: 'PUT', body: { checklist } }),
  getAttendance:    (date)     => request(`/clock/attendance?date=${date}`),
  // Contractor's own attendance records (office cleaner / hourly "My Records").
  getMyRecords:     (from, to) => request(`/clock/my-records?from=${from}&to=${to}`),
  // Date-range attendance — for Week / Month / Person views. Optionally filter
  // to one contractor by passing user_id.
  getAttendanceRange: ({ from, to, user_id } = {}) => {
    const qs = new URLSearchParams({ from, to });
    if (user_id) qs.set('user_id', user_id);
    return request(`/clock/attendance/range?${qs.toString()}`);
  },
  createAttendance: (body)     => request(`/clock/attendance`, { method: 'POST', body }),
  deleteAttendance: (id)       => request(`/clock/attendance/${id}`, { method: 'DELETE' }),
  updateAttendance: (id, body) => request(`/clock/attendance/${id}`, { method: 'PATCH', body }),

  // Contractors (main)
  getContractors:    ()       => request('/contractors'),
  getActiveContractors: ()    => request('/contractors/active'),
  addContractor:   (data)     => request('/contractors', { method: 'POST', body: data }),
  updateContractor: (id, data)=> request(`/contractors/${id}`, { method: 'PUT', body: data }),
  deleteContractor: (id)      => request(`/contractors/${id}`, { method: 'DELETE' }),
  getContractor:   (id)       => request(`/contractors/${id}`),

  // Signup token flow
  createSignupToken: (data)    => request('/contractors/signup-token', { method: 'POST', body: data }),
  validateSignupToken: (token) => request(`/contractors/signup/${token}`),
  completeSignup: (token, data)=> request(`/contractors/signup/${token}`, { method: 'POST', body: data }),

  // Checklist tasks (office cleaner)
  getChecklistTasks:   ()      => request('/contractors/checklist/tasks'),
  updateChecklistTasks:(tasks) => request('/contractors/checklist/tasks', { method: 'PUT', body: { tasks } }),

  // Profile (self-service)
  getProfile:    ()     => request('/profile'),
  updateProfile: (data) => request('/profile', { method: 'PUT', body: data }),

  // Invoices
  getInvoice: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/invoices${qs ? '?' + qs : ''}`);
  },
  getInvoiceContractors: () => request('/invoices/contractors'),

  // Email server diagnostics — live SMTP probe, so a failing send can be
  // told apart from a redirected one without shell access to the server.
  // Statement — earnings from the app, payments from Zoho Books.
  getMyStatement: (fy) => request(`/statement/me${fy ? `?fy=${fy}` : ''}`),
  getMyStatementYears: () => request('/statement/years'),

  getEmailStatus: () => request('/email/status'),
  sendTestEmail:  (to) => request('/email/test', { method: 'POST', body: { to } }),

  // The contractor signs their own invoice — the server takes the identity
  // from the session, so no contractor_id is sent.
  signInvoice: (week_start, signature_png, typed_name, total) =>
    request('/invoices/sign', { method: 'POST', body: { week_start, signature_png, typed_name, total } }),

  approveInvoice: (contractor_id, week_start, approved = true) =>
    request('/invoices/approve', { method: 'POST', body: { contractor_id, week_start, approved } }),
  getInvoiceLastWeek: (contractorId) => request(`/invoices/last-week/${contractorId}`),
  addInvoiceExtra: (data) => request('/invoices/extras', {
    method: 'POST',
    body: data,
  }),
  deleteInvoiceExtra: (id) => request(`/invoices/extras/${id}`, { method: 'DELETE' }),
  sendInvoiceEmail: (invoice, to_email, pdf) => request('/invoices/send-email', {
    method: 'POST',
    // request() stringifies for us — stringifying here sent a double-encoded
    // body and the server saw no invoice at all.
    body: { invoice, to_email, pdf_base64: pdf?.base64, pdf_filename: pdf?.filename },
  }),

  // Backward-compat aliases (used by older components)
  getEmployees:    ()       => request('/contractors'),
  getActiveEmployees: ()    => request('/contractors/active'),
  addEmployee:   (data)     => request('/contractors', { method: 'POST', body: data }),
  updateEmployee:(id, data) => request(`/contractors/${id}`, { method: 'PUT', body: data }),
  deleteEmployee:(id)       => request(`/contractors/${id}`, { method: 'DELETE' }),

  // Public Holidays
  getPublicHolidays: (year) => request(`/public-holidays${year ? '?year=' + year : ''}`),
  checkPublicHoliday: (date) => request(`/public-holidays/check/${date}`),
  addPublicHoliday: (date, name) => request('/public-holidays', { method: 'POST', body: { date, name } }),
  deletePublicHoliday: (id) => request(`/public-holidays/${id}`, { method: 'DELETE' }),

  // Daily Contracts
  getDailyContracts: (week) => request(`/daily-contracts?week=${week}`),
  confirmDailyContract: (id) => request(`/daily-contracts/${id}/confirm`, { method: 'PUT' }),
  cancelDailyContract:  (id) => request(`/daily-contracts/${id}/cancel`,  { method: 'PUT' }),
  undoCancelDailyContract: (id) => request(`/daily-contracts/${id}/undo-cancel`, { method: 'PUT' }),

  // Form Config — car models
  getCarModels:    ()          => request('/form-config/models'),
  addCarModel:     (data)      => request('/form-config/models', { method: 'POST', body: data }),
  updateCarModel:  (id, model) => request(`/form-config/models/${id}`, { method: 'PUT', body: { model } }),
  deleteCarModel:  (id)        => request(`/form-config/models/${id}`, { method: 'DELETE' }),

  // Bank Statements
  getStatements:     ()              => request('/statements'),
  getStatement:      (id)            => request(`/statements/${id}`),
  createStatement:   (data)          => request('/statements', { method: 'POST', body: data }),
  updateTransaction: (stmtId, txnId, data) => request(`/statements/${stmtId}/transactions/${txnId}`, { method: 'PUT',  body: data }),
  // Itemised splits — replace all splits for a transaction in one shot.
  // Pass splits: [] to clear (reverts to single-category mode).
  setTransactionSplits: (txnId, splits) =>
    request(`/statements/transactions/${txnId}/splits`, { method: 'PUT', body: { splits } }),
  deleteStatement:   (id)            => request(`/statements/${id}`, { method: 'DELETE' }),
  getWeeklyExpenses: (week_number, week_year) =>
    request(`/statements/meta/weekly-expenses?week_number=${week_number}&week_year=${week_year}`),
  getAllTransactions: () => request('/statements/meta/all-transactions'),
  // Bank accounts (4 fixed: ACTS / ACTS TX / GST / Supporting)
  getBankAccounts: () => request('/statements/bank-accounts'),
  getBankAccountBalances: () => request('/statements/bank-accounts/balances'),
  rematchTransfers: () => request('/statements/bank-accounts/rematch', { method: 'POST' }),
  updateStatement: (id, data) => request(`/statements/${id}`, { method: 'PUT', body: data }),

  // Expenses (Finance → Expenses tab)
  getExpenseCategories: () => request('/expenses/categories'),
  getExpenses: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/expenses${qs ? '?' + qs : ''}`);
  },
  getExpenseTotals: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/expenses/totals${qs ? '?' + qs : ''}`);
  },
  createExpense: (data)     => request('/expenses', { method: 'POST', body: data }),
  updateExpense: (id, data) => request(`/expenses/${id}`, { method: 'PUT', body: data }),
  deleteExpense: (id)       => request(`/expenses/${id}`, { method: 'DELETE' }),
  // Zoho Books expense sync (Finance Phase 2)
  expenseZohoStatus:   ()     => request('/expenses/zoho-status'),
  expenseZohoSync:     ()     => request('/expenses/zoho-sync', { method: 'POST' }),
  expenseZohoAccounts: ()     => request('/expenses/zoho-accounts'),
  expenseZohoMapping:  (body) => request('/expenses/zoho-mapping', { method: 'PUT', body }),

  // Assets (Finance → Assets tab)
  getAssetMeta: () => request('/assets/categories'),
  getAssets: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/assets${qs ? '?' + qs : ''}`);
  },
  getAssetTotals: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/assets/totals${qs ? '?' + qs : ''}`);
  },
  getAssetDepreciation: (id) => request(`/assets/${id}/depreciation`),
  createAsset: (data)     => request('/assets', { method: 'POST', body: data }),
  updateAsset: (id, data) => request(`/assets/${id}`, { method: 'PUT', body: data }),
  deleteAsset: (id)       => request(`/assets/${id}`, { method: 'DELETE' }),

  // Finance — Accountant Pack
  getAccountantPack: (fy) => request(`/finance/accountant-pack?fy=${fy}`),
  getBankReconciliations: () => request('/finance/bank-reconciliations'),
  createBankReconciliation: (data) => request('/finance/bank-reconciliations', { method: 'POST', body: data }),
  updateBankReconciliation: (id, data) => request(`/finance/bank-reconciliations/${id}`, { method: 'PUT', body: data }),
  deleteBankReconciliation: (id) => request(`/finance/bank-reconciliations/${id}`, { method: 'DELETE' }),
  // Returns a Blob — the request helper handles non-JSON responses transparently.
  generateAccountantPackZip: (fy) =>
    request('/finance/generate-pack', { method: 'POST', body: { fy } }),

  // Web Push notifications — clock-in reminders for hourly + OC contractors.
  getVapidKey:       () => request('/push/vapid-key'),
  pushStatus:        () => request('/push/status'),
  subscribePush:     (sub) => request('/push/subscribe', { method: 'POST', body: sub }),
  unsubscribePush:   (endpoint) => request('/push/subscribe', { method: 'DELETE', body: { endpoint } }),
  testPush:          () => request('/push/test', { method: 'POST' }),
  triggerPushReminders: () => request('/push/send-clock-in-reminders', { method: 'POST' }),

  // Zoho Books invoice automation
  zohoStatus:        () => request('/zoho/status'),
  createZohoInvoice: (body) => request('/zoho/create-invoice', { method: 'POST', body }),
  // What the invoice will be numbered — asked of the server rather than
  // guessed, so the preview can't disagree with what gets filed.
  zohoInvoiceMeta: (params) =>
    request(`/zoho/invoice-meta?${new URLSearchParams(params).toString()}`),
  // Profit & Loss for the dashboard — Zoho is source of truth (handles GST
  // correctly and avoids the sub-account double-counting bug from the local
  // bank-statement based computeBAS).
  zohoPlReport:      (from, to) => request(`/zoho/pl-report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  // Phase-2 reconciliation: Zoho vs local statement totals + a list of
  // likely mis-categorised transactions the boss can fix one click each.
  zohoPlComparison:  (from, to) => request(`/zoho/pl-comparison?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  // Deeper investigation — per-bank breakdown, top Income txns, dup groups.
  zohoIncomeDiag:    (from, to) => request(`/zoho/income-diagnostic?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  // Duplicate-statement cleanup (Plan B). Find = read-only discovery,
  // delete = destructive, requires confirm: 'YES' AND server re-checks
  // every ID is still fully duplicate before touching the DB.
  zohoFindDupStatements: ()    => request('/zoho/find-duplicate-statements'),
  zohoDeleteStatements:  (ids) => request('/zoho/delete-statements', { method: 'POST', body: { ids, confirm: 'YES' } }),

  // Buffing Authorization
  submitBuffing:   (data) => request('/buffing', { method: 'POST', body: data }),
  getBuffingAll:   (status) => request(`/buffing${status && status !== 'all' ? '?status=' + status : ''}`),
  getMyBuffing:    ()     => request('/buffing/my'),
  // The percentages the Buffing form offers. Chosen, not summed from parts.
  getBuffingTiers: ()      => request('/buffing/tiers'),
  saveBuffingTiers: (tiers) => request('/buffing/tiers', { method: 'PUT', body: { tiers } }),
  approveBuffing:  (id, overrides = {}) => request(`/buffing/${id}/approve`, { method: 'POST', body: overrides }),
  resendBuffingEmail: (id, overrides = {}) => request(`/buffing/${id}/resend-email`, { method: 'POST', body: overrides }),
  rejectBuffing:   (id)   => request(`/buffing/${id}/reject`,  { method: 'POST' }),
  deleteBuffing:   (id)   => request(`/buffing/${id}`,         { method: 'DELETE' }),

  // Contractor Agreements
  submitAgreement:  (data) => request('/contractor-agreement', { method: 'POST', body: data }),
  getAgreements:    ()     => request('/contractor-agreement'),
  deleteAgreement:  (id)   => request(`/contractor-agreement/${id}`, { method: 'DELETE' }),

  // ── Customers (multi-customer, Phase 3) ──────────────────────────────────
  listCustomers:        (status) => request(`/customers${status ? `?status=${status}` : ''}`),
  getCustomer:          (id)     => request(`/customers/${id}`),
  createCustomer:       (body)   => request('/customers', { method: 'POST', body }),
  updateCustomer:       (id, body) => request(`/customers/${id}`, { method: 'PUT', body }),
  deleteCustomer:       (id)     => request(`/customers/${id}`, { method: 'DELETE' }),
  myAccessibleCustomers: ()      => request('/customers/my/accessible'),
  // Active services for a customer — used by the Log Job form to list the
  // customer's services (including ones created in the Customers tab).
  getCustomerServices:   (id)    => request(`/customers/${id}/services`),
  // The private price list, for the job form when "Private car" is ticked.
  getPrivateServices:    ()      => request('/customers/private/services'),

  // ── Melville Workflow integration ────────────────────────────────────────
  getHourlyProductionWeek: () => request('/jobs/hourly-production-week'),
  getMyEarnings:        () => request('/jobs/my-earnings'),
  melvilleStatus:       () => request('/melville/status'),
  melvilleDetailQueue:  () => request('/melville/detail-queue'),
  // Write-back: pick → In Progress; submit → Detailed (done).
  melvilleMarkInProgress: (body) => request('/melville/mark-in-progress', { method: 'POST', body }),
  melvilleMarkDetailed:   (body) => request('/melville/mark-detailed',    { method: 'POST', body }),
  melvilleUnpick:         (body) => request('/melville/unpick',           { method: 'POST', body }),
  // Pause / resume the detail clock — they stop for lunch mid-car and the
  // break shouldn't count as work.
  melvillePause:          (body) => request('/melville/pause',            { method: 'POST', body }),
  melvilleResume:         (body) => request('/melville/resume',           { method: 'POST', body }),
  // Detail timing: stop the clock on Submit tap; admin reads durations.
  melvilleStopTiming:     (body) => request('/melville/stop-timing',      { method: 'POST', body }),
  melvilleTimings:        () => request('/melville/timings'),
  melvilleMyCompleted:    (from, to) => request(`/melville/my-completed${from ? `?from=${from}&to=${to || from}` : ''}`),
  // Departments
  addCustomerDept:      (id, body)         => request(`/customers/${id}/departments`, { method: 'POST', body }),
  updateCustomerDept:   (id, deptId, body) => request(`/customers/${id}/departments/${deptId}`, { method: 'PUT', body }),
  deleteCustomerDept:   (id, deptId)       => request(`/customers/${id}/departments/${deptId}`, { method: 'DELETE' }),
  // Pricing / services
  addCustomerPricing:    (id, body)          => request(`/customers/${id}/pricing`, { method: 'POST', body }),
  updateCustomerPricing: (id, priceId, body) => request(`/customers/${id}/pricing/${priceId}`, { method: 'PUT', body }),
  deleteCustomerPricing: (id, priceId)       => request(`/customers/${id}/pricing/${priceId}`, { method: 'DELETE' }),
  copyCustomerPricing:   (id, srcId)         => request(`/customers/${id}/copy-pricing-from/${srcId}`, { method: 'POST' }),
  // Contractor access
  grantCustomerAccess:  (id, body)            => request(`/customers/${id}/access`, { method: 'POST', body }),
  revokeCustomerAccess: (id, contractorId)    => request(`/customers/${id}/access/${contractorId}`, { method: 'DELETE' }),

  // ── Private customers ───────────────────────────────────────────────────
  // Their own business line: a person, a quote, a discount, and a week to pay.
  // Payment status comes back from Zoho Books, never typed in here.
  privateClients:       ()        => request('/private/clients'),
  privateClient:        (id)      => request(`/private/clients/${id}`),
  createPrivateClient:  (body)    => request('/private/clients', { method: 'POST', body }),
  updatePrivateClient:  (id, body) => request(`/private/clients/${id}`, { method: 'PUT', body }),
  privateJobs:          (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/private/jobs${qs ? '?' + qs : ''}`);
  },
  setPrivateJobClient:  (jobId, private_client_id) =>
    request(`/private/jobs/${jobId}`, { method: 'PATCH', body: { private_client_id } }),
  // Quotes: the private job before it happens. Sent as a Zoho estimate, and
  // once accepted and given a day it becomes a card the detailers can pick up.
  privateQuotes:        (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/private/quotes${qs ? '?' + qs : ''}`);
  },
  privateQuote:         (id)      => request(`/private/quotes/${id}`),
  bookedPrivateCars:    ()        => request('/private/quotes/booked'),
  createPrivateQuote:   (body)    => request('/private/quotes', { method: 'POST', body }),
  updatePrivateQuote:   (id, body) => request(`/private/quotes/${id}`, { method: 'PUT', body }),
  patchPrivateQuote:    (id, body) => request(`/private/quotes/${id}`, { method: 'PATCH', body }),
  sendPrivateQuote:     (id, to)  => request(`/private/quotes/${id}/send`, { method: 'POST', body: { to } }),
  syncPrivateQuote:     (id)      => request(`/private/quotes/${id}/sync`, { method: 'POST' }),
  deletePrivateQuote:   (id)      => request(`/private/quotes/${id}`, { method: 'DELETE' }),

  privateInvoices:      (client_id) =>
    request(`/private/invoices${client_id ? `?client_id=${client_id}` : ''}`),
  createPrivateInvoice: (body)    => request('/private/invoices', { method: 'POST', body }),
  // For invoices raised by hand in Zoho — links by number instead of making a
  // second one.
  linkPrivateInvoice:   (body)    => request('/private/invoices/link', { method: 'POST', body }),
  syncPrivateInvoice:   (id)      => request(`/private/invoices/${id}/sync`,  { method: 'POST' }),
  emailPrivateInvoice:  (id, to)  => request(`/private/invoices/${id}/email`, { method: 'POST', body: { to } }),
  deletePrivateInvoice: (id)      => request(`/private/invoices/${id}`, { method: 'DELETE' }),

  // Chart data
  weeklyGross: (years, customer_id) => request(`/jobs/weekly-gross?years=${years.join(',')}${customer_id ? `&customer_id=${customer_id}` : ''}`),
  getWeeklyKPIs: (weeks = 12, customer_id) => request(`/jobs/weekly-kpis?weeks=${weeks}${customer_id ? `&customer_id=${customer_id}` : ''}`),
  getWeeklyJobsBreakdown: (weekNumber, weekYear, customer_id) =>
    request(`/jobs/weekly-jobs-breakdown?week_number=${weekNumber}&week_year=${weekYear}${customer_id ? `&customer_id=${customer_id}` : ''}`),

  // ── Insurance Centre ────────────────────────────────────────────────────
  // Contractor uploads Certificate of Currency PDFs; admin reviews status,
  // gets email digest of expiring/expired/missing certificates.
  insuranceConfig:        ()        => request('/insurance/config'),
  saveInsuranceConfig:    (body)    => request('/insurance/config', { method: 'PUT', body }),
  uploadInsuranceCertificate: (body) => request('/insurance/certificate', { method: 'POST', body }),
  myInsuranceCertificates: ()       => request('/insurance/my'),
  // Admin-only
  insuranceAdminOverview: ()        => request('/insurance/admin/overview'),
  insuranceForContractor: (subId)   => request(`/insurance/admin/contractor/${subId}`),
  deleteInsuranceCertificate: (id)  => request(`/insurance/certificate/${id}`, { method: 'DELETE' }),
  triggerInsuranceDigest: ()        => request('/insurance/send-expiry-reminders', { method: 'POST' }),
  // URL helper for inline preview / "Open" links — auth happens via cookie/
  // header from the same origin, so this is just a string the browser can
  // open in a new tab.
  insuranceFileUrl: (id) => `/api/insurance/certificate/${id}/file`,

  // Generic request (used by BuffingForm/BuffingAdmin)
  request,

  // Utility
  getWeekStart,
};
